import { useEffect, useMemo, useState } from 'react';
import { Box, ListItemIcon, ListItemText, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, GridColumnProps } from '@roc/ui';
import { observer } from 'mobx-react';
import { ProtectiveAdvancesGrid } from '../protectiveAdvancesGrid';
import { dateFilterProps, filterProps, noOpComparator, selectFilterProps, selectSearchFilterProps } from '@roc/ui/utils';
import { CounterPartyExpensesTab, getPayableReceivablesStatusOptions, TYPES_OF_FEE } from '../../types/types';
import { usePayablesDashboardStore } from '../../hooks/usePayablesDashboardStore';
import { CloudDownload, Check } from '@material-ui/icons';
import { VendorPaymentsToolbar } from './vendorPaymentsToolbar';
import { VendorPaymentDetailsModal } from './vendorPaymentDetailsModal';

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '1400px',
  },
  paper: {
    boxShadow: 'none !important',
    border: '1px solid #cdcdcd',
    padding: '0px',
    borderRadius: '0 !important',
  },
}));

export const VendorPaymentsDashboard = observer(() => {
  const classes = useStyles();
  const { vendorPaymentDashboardStore, vendorPaymentDashboardStore: { tab } } = usePayablesDashboardStore();

  useEffect(() => {
    vendorPaymentDashboardStore.fetchEntityNames();
  }, []);

  const actions = (record, handleMenuClose) => {

    return (
      [
        <MenuItem
          onClick={() => {
            vendorPaymentDashboardStore.downloadVendorPaymentDocuments([record.recordId]);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Invoices Files</ListItemText>
        </MenuItem>,
        <MenuItem
          onClick={() => {
            vendorPaymentDashboardStore.exportVendorPaymentInvoicesData(record.recordId);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Invoices Report</ListItemText>
        </MenuItem>,
      ]);
  }
  const COLUMNS: GridColumnProps[] = [
    {
      field: 'recordId',
      headerName: 'Payment Id',
      maxWidth: 120,
      comparator: noOpComparator,
      cellRenderer: 'linkColumnCellRenderer',
      ...filterProps()
    },
    {
      field: 'totalBills',
      headerName: 'Total Bills',
      maxWidth: 100,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'entity',
      headerName: 'Vendor',
      minWidth: 125,
      maxWidth: 235,
      comparator: noOpComparator,
      ...selectSearchFilterProps(null, () => vendorPaymentDashboardStore.vendorOptions)
    },
    {
      field: 'bankInfoVerified',
      headerName: 'Bank Information',
      minWidth: 125,
      maxWidth: 145,
      comparator: noOpComparator,
      cellRenderer: 'bankInfoCellRenderer',
      ...filterProps(),
    },
    {
      field: 'status',
      headerName: 'Payable Status',
      maxWidth: 125,
      comparator: noOpComparator,
      cellRenderer: 'statusCellRenderer',
      ...selectFilterProps(getPayableReceivablesStatusOptions(tab)),
    },
    {
      field: 'amount',
      headerName: 'Total Bill Amount',
      minWidth: 125,
      maxWidth: 145,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'totalReimbursedAmount',
      headerName: 'Total Reimbursed Amount',
      minWidth: 125,
      maxWidth: 200,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'frontingPercentage',
      headerName: 'Fronting %',
      minWidth: 125,
      maxWidth: 125,
      cellRenderer: 'percentageCellRenderer',
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'createdDate',
      headerName: 'Creation Date',
      minWidth: 125,
      maxWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'paidDate',
      headerName: 'Paid Date',
      minWidth: 125,
      maxWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'comments',
      headerName: 'Comments',
      minWidth: 150,
      comparator: noOpComparator,
      editable: true,
      cellEditor: 'commentEditor',
      cellEditorParams: {
        type: tab,
      },
      ...filterProps()
    },
    {
      field: 'leadId',
      headerName: 'Action',
      minWidth: 80,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      width: 80,
      pinned: 'right',
      lockPinned: true,
    },
  ];

  return (
    <>
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex={1}>
          <Paper className={classes.paper}>
            <ProtectiveAdvancesGrid
              counterPartyExpenseStore={vendorPaymentDashboardStore}
              columns={COLUMNS}
              toolbar={<VendorPaymentsToolbar />}
              selectModeEnabled={true}
              actions={actions}
            />
          </Paper>
        </Box>
      </Box>
      <VendorPaymentDetailsModal />
    </>
  );
});
